import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextAreaField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { REPLACEMENT_REJECTED_MESSAGE } from '../constants';
import { useRejectReplacement } from '../replacements/hooks';
import { rejectReplacementSchema } from '../replacements/schemas';
import type { RejectReplacementValues } from '../replacements/types';

import styles from './reject-replacement-modal.module.scss';

export type RejectReplacementModalProps = {
  open: boolean;
  id: string;
  onClose: () => void;
  onRejected: () => void;
};

/** "Reject Request" popup (Figma 30:412300, ADR-0019). Required reason, hard-capped at 2000
 *  characters; on success the mutation seeds the detail cache and invalidates the list. */
export function RejectReplacementModal({
  open,
  id,
  onClose,
  onRejected,
}: RejectReplacementModalProps) {
  const form = useForm<RejectReplacementValues>({
    resolver: zodResolver(rejectReplacementSchema),
    defaultValues: { reason: '' },
    mode: 'onChange',
  });
  const reject = useRejectReplacement(id);
  const toast = useToast();

  const submit = (values: RejectReplacementValues) =>
    reject.mutate(values, {
      onSuccess: () => {
        toast.success({ title: REPLACEMENT_REJECTED_MESSAGE });
        form.reset({ reason: '' });
        onRejected();
        onClose();
      },
      onError: (e) =>
        toast.error({
          title:
            e instanceof ApiError ? e.message : 'Could not reject the request. Please try again.',
        }),
    });

  return (
    <FormModal
      form={form}
      open={open}
      onClose={onClose}
      size="lg"
      centered={false}
      title={<h3 className={styles.title}>Reject Request</h3>}
      submitText="Reject"
      onSubmit={submit}
      loading={reject.isPending}
    >
      <div className={styles.body}>
        <TextAreaField
          name="reason"
          control={form.control}
          label="Enter Reason"
          placeholder="Enter"
          rows={5}
          maxLength={2000}
          showCount
          required
        />
      </div>
    </FormModal>
  );
}
